/**
 * WorkforcePage — Unified actor + team management hub (DRR-134/135).
 *
 * Tabs are driven by the backend setting bundle `actor_type_labels`.
 * For ALF: Staff | Residents | Organization | Teams
 * For Home Care: Caregivers | Clients | Organization | Teams
 *
 * Each actor-type tab renders ActorManagementPage filtered by that type.
 * The Teams tab renders TeamManagementPage (unchanged).
 *
 * DUMB FRONTEND: Tab labels come from backend, not frontend constants.
 */

import { useMemo } from 'react';
import { Tabs, Skeleton, Stack } from '@mantine/core';
import { IconUser, IconUsers, IconBuilding, IconUserStar } from '@tabler/icons-react';

import { GqlActorType, useGetSettingNavigationQuery } from '@/generated/graphql';
import { ActorManagementPage } from '@/pages/actors/ActorManagementPage';
import { TeamManagementPage } from '@/pages/team/TeamManagementPage';

// ============================================================================
// Icon mapping — deterministic from ActorType, no domain assumptions
// ============================================================================

const ACTOR_TYPE_ICON: Record<string, typeof IconUser> = {
  HUMAN: IconUser,
  SUBJECT: IconUserStar,
  COMPOSITE: IconBuilding,
  EXTERNAL: IconUsers,
};

// Map setting bundle key → GqlActorType enum value
const ACTOR_TYPE_MAP: Record<string, GqlActorType> = {
  HUMAN: GqlActorType.HUMAN,
  SUBJECT: GqlActorType.SUBJECT,
  COMPOSITE: GqlActorType.COMPOSITE,
  EXTERNAL: GqlActorType.EXTERNAL,
  AI: GqlActorType.AI,
  SYSTEM: GqlActorType.SYSTEM,
  DEVICE: GqlActorType.DEVICE,
  CUSTOM: GqlActorType.CUSTOM,
};

// ============================================================================
// Default labels (fallback when setting bundle has no actor_type_labels)
// ============================================================================

const DEFAULT_ACTOR_TYPE_LABELS: Record<string, string> = {
  HUMAN: 'Staff',
  SUBJECT: 'Subjects',
  COMPOSITE: 'Organization',
};

export function WorkforcePage() {
  const { data: settingData, loading } = useGetSettingNavigationQuery({
    fetchPolicy: 'cache-first',
  });

  // DRR-134/135: Actor type labels from backend setting bundle.
  // Fallback to defaults if setting bundle doesn't define actor_type_labels.
  const actorTypeTabs = useMemo(() => {
    const backendLabels = settingData?.settingBundle?.actorTypeLabels as
      | Record<string, string>
      | null
      | undefined;
    const labels = backendLabels && Object.keys(backendLabels).length > 0
      ? backendLabels
      : DEFAULT_ACTOR_TYPE_LABELS;

    return Object.entries(labels).map(([typeKey, label]) => ({
      typeKey,
      label,
      actorType: ACTOR_TYPE_MAP[typeKey] ?? GqlActorType.HUMAN,
      Icon: ACTOR_TYPE_ICON[typeKey] ?? IconUser,
    }));
  }, [settingData]);

  if (loading) {
    return (
      <Stack gap="md" p="md">
        <Skeleton height={40} radius="sm" />
        <Skeleton height={300} radius="sm" />
      </Stack>
    );
  }

  return (
    <Tabs defaultValue={actorTypeTabs[0]?.typeKey ?? 'HUMAN'} keepMounted={false}>
      <Tabs.List px="md" pt="md">
        {actorTypeTabs.map(({ typeKey, label, Icon }) => (
          <Tabs.Tab key={typeKey} value={typeKey} leftSection={<Icon size={16} />}>
            {label}
          </Tabs.Tab>
        ))}
        <Tabs.Tab value="teams" leftSection={<IconUsers size={16} />}>
          Teams
        </Tabs.Tab>
      </Tabs.List>

      {actorTypeTabs.map(({ typeKey, label, actorType }) => (
        <Tabs.Panel key={typeKey} value={typeKey}>
          <ActorManagementPage actorType={actorType} typeLabel={label} />
        </Tabs.Panel>
      ))}

      <Tabs.Panel value="teams">
        <TeamManagementPage />
      </Tabs.Panel>
    </Tabs>
  );
}

export default WorkforcePage;
